import { Card, Typography } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { TREND_DAYS } from "../hooks/useDashboard";
import type { AttendanceTrend, DashboardPayload, TrendPoint } from "../types";
import { davomatDarajasi, davomatDelta, foizMatn, mergeBugun } from "../utils/davomatDarajasi";
import { Sparkline } from "./Sparkline";

const { Text } = Typography;

export interface AttendanceRateHeroProps {
	/**
	 * ⚠️ `DashboardPayload` NING KESIMI, to'lig'i EMAS: komponent faqat
	 * uch maydonni o'qiydi va to'liq tip talab qilinsa har testda 19
	 * maydonli fixture kerak bo'lardi (yangi skalyar qo'shilganda esa
	 * hammasi qizil bo'lardi).
	 */
	sanoq: Pick<DashboardPayload, "date" | "plannedShifts" | "boshlangan"> | undefined;
	trend: AttendanceTrend | undefined;
	/** ⚠️ Trend so'rovining O'Z holati — sanoqlar `Alert` i uni QOPLAMAYDI. */
	trendError?: boolean;
	/** Tanlangan sana (`undefined` — «Bugun» rejimi). */
	kun?: string;
}

/**
 * Trend seriyasini KUNLAR bo'yicha ZICHLASHTIRADI.
 *
 * ⚠️⚠️ **Seriyada BO'SHLIQ BOR va uni NOL bilan to'ldirib bo'lmaydi.**
 * Backend kogortasi bo'sh kunni (dam olish) UMUMAN qator bermaydi
 * (`AttendanceTrendSchema`: «Klient bo'shliqni NOL bilan
 * TO'LDIRMASLIGI SHART — sparkline har yakshanba 0% ga qulardi»).
 * Yo'q kun `null` bo'ladi va `Sparkline` chiziqni o'sha yerda UZADI.
 *
 * ⚠️ Zichlashtirish `to` dan orqaga sanaladi, mavjud nuqtalar
 * TARTIBIDAN emas — aks holda bo'shliq vaqt o'qini siqib, tanaffusni
 * yashirardi.
 */
function seriyaQur(points: TrendPoint[], to: string, days: number): (number | null)[] {
	const oxirgi = dayjs(to);
	const xarita = new Map(points.map((nuqta) => [nuqta.date, nuqta]));

	return Array.from({ length: days }, (_, indeks) => {
		const sana = oxirgi.subtract(days - 1 - indeks, "day").format("YYYY-MM-DD");
		const nuqta = xarita.get(sana);

		return nuqta ? davomatDarajasi(nuqta.boshlangan, nuqta.plannedShifts) : null;
	});
}

/**
 * Davomat darajasi — panelning qahramon kartasi.
 *
 * ⚠️⚠️ **SURAT `boshlangan`, MAXRAJ `plannedShifts`. `kelgan`
 * ISHLATILMAYDI.** `hisoblaStatus` ustuvorligida `davom_etmoqda`
 * `kechikkan` ni, `kechikkan` esa `erta_ketgan` ni SOYALAYDI
 * (`packages/domain/src/attendance.ts`), ya'ni `status = 'kelgan'`
 * faqat «kelib, ketib, kechikmagan va erta ketmagan» kun.
 * `kelgan/plannedShifts` butun ish kuni davomida haqiqiy darajani past
 * ko'rsatib, kechqurun o'z-o'zidan «tuzalardi» — hech qanday test
 * qizil bermasdi.
 *
 * ⚠️⚠️ **Sparkline'ning oxirgi nuqtasi hero raqami bilan TENG bo'lishi
 * SHART.** Hero `/dashboard` dan (20 s poll), trend
 * `/dashboard/trend` dan (10 daqiqa `staleTime`) keladi —
 * sinxronlanmasa bitta savolga ikki javob berilardi. Yechim:
 * `mergeBugun()` CHIZISHDAN OLDIN.
 *
 * ⚠️ **Trend so'rovi yiqilsa hero RAQAMI QOLADI** — sparkline va delta
 * yo'qoladi, o'rnida kichik izoh. Sanoqlar `Alert` i buni qoplamaydi.
 *
 * ⚠️ **Yo'nalish RANG bilan berilmaydi** — strelka + ishora (`↑ 1,4
 * p.p.`). Kanvasning yashil `hero-delta` si taqiqlangan yashil↔qizil
 * juftligini tiklardi (deuteranopiyada ΔE 4,1).
 */
export function AttendanceRateHero({ sanoq, trend, trendError, kun }: AttendanceRateHeroProps) {
	const { t } = useTranslation("dashboard");
	const foiz = sanoq ? davomatDarajasi(sanoq.boshlangan, sanoq.plannedShifts) : null;

	const birlashgan = trend ? mergeBugun(trend.points, sanoq) : [];
	const seriya = trend ? seriyaQur(birlashgan, trend.to, trend.days) : [];
	const delta = sanoq && trend ? davomatDelta(birlashgan, sanoq.date) : null;

	// ⚠️ O'ng yorliq: «Kecha» rejimida trend `to=2026-01-14` bilan
	// so'raladi, ya'ni qattiq yozilgan «Bugun» YOLG'ON bo'lardi.
	const ongYorliq = kun ? dayjs(kun).format("DD.MM") : t("hero.today");

	return (
		<Card styles={{ body: { minHeight: 108 } }}>
			<div className="flex flex-wrap items-start justify-between gap-6">
				<div>
					<Text type="secondary" className="block text-xs">
						{t("hero.rateLabel")}
					</Text>

					<div className="flex items-baseline gap-2">
						<span className="font-semibold text-4xl tabular-nums">{foizMatn(foiz)}</span>

						{delta === null ? null : (
							<span className="text-xs tabular-nums">
								{delta > 0 ? "↑" : delta < 0 ? "↓" : "→"}{" "}
								{Math.abs(delta).toFixed(1).replace(".", ",")} p.p.
							</span>
						)}
					</div>

					{sanoq ? (
						<Text type="secondary" className="mt-1 block text-xs">
							{t("hero.basis", { planned: sanoq.plannedShifts, started: sanoq.boshlangan })}
							{/*
								⚠️ Yorliq HISOBGA mos: baza — MEDIANA, «kecha» EMAS
								(`davomatDelta`). Kanvasdagi «kechagi kunga nisbatan»
								matni NUSXA KO'CHIRILMAYDI — u hisobga zid bo'lardi.
								Oyna uzunligi konstantadan, satrdan EMAS.
							*/}
							{delta === null ? null : t("hero.basisMedian", { days: TREND_DAYS })}
						</Text>
					) : null}
				</div>

				<div>
					{trend ? (
						<Sparkline points={seriya} ariaLabel={t("hero.ariaLabel", { value: TREND_DAYS })} />
					) : (
						<div style={{ width: 300, height: 52 }} className="flex items-center">
							{trendError ? (
								<Text type="secondary" className="text-xs">
									{t("hero.trendNotLoaded")}
								</Text>
							) : null}
						</div>
					)}

					<div className="mt-1.5 flex justify-between">
						<Text type="secondary" className="text-xs">
							{t("hero.daysAgo", { value: TREND_DAYS })}
						</Text>
						<Text type="secondary" className="text-xs">
							{ongYorliq}
						</Text>
					</div>
				</div>
			</div>
		</Card>
	);
}
